import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import cyberResilienceImage from "../../assets/CyberResilienceViewLight.svg";
import darklogo from "../../assets/CyberResilienceViewDark.svg";
import moment from "moment";
import eventEmitter from "../utils/eventEmitter";
// import cyberResilienceImageDark from "../../assets/CyberResilienceViewsDark.png";

interface ChartDataPoint {
  x: number;
  y: number;
}

interface CVETimelineViewProps {
  height?: string | number;
  selectedCVE?: string;
  loading?: boolean;
}

const CVETimelineView: React.FC<CVETimelineViewProps> = ({ height = "100%", selectedCVE, loading: externalLoading }) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [cveData, setCVEData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
      const handleFilterSaved = () => {
        setLoading(true);
        setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
      };
  
      eventEmitter.on("filterSaved", handleFilterSaved);
  
      return () => {
        eventEmitter.off("filterSaved", handleFilterSaved);
      };
    }, []);

  useEffect(() => {
    
    const fetchData = async () => {
      setLoading(true);
      const endDate = moment().format("YYYY-MM-DD");
      const startDate = moment().subtract(1, 'year').format("YYYY-MM-DD");
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const payload: any = {
          duration: "custom_date_range",
          start_date: startDate,
          end_date: endDate,
          timezone: timeZone,
          module: "on_premise",
          cve: true,
          intelli_score: false,
          rating: false
      };
     
      try {
          const response = await axiosInstance.post(END_POINTS.DASHBOARD_CYBER_RESILIENCE_TREND,payload);
          
          if (response.data && response.data.status === 200) {
            const cveTransformed = response?.data?.data?.graph_data.map(
              (item: any) => ({
                x: new Date(item.date_time).getTime(),
                y: item.cve_score,
              })
            );
            setCVEData(cveTransformed);
            console.log(cveTransformed);
            console.log(cveData);
          } 
      } catch (error) {
        console.error("Error fetching CVE timeline data:", error);
        // Generate mock data as fallback
        // generateMockData();
      } finally {
        setLoading(false);
      }
    };

    // Generate mock data similar to the CyberResilienceView component
    // const generateMockData = () => {
    //   const base = new Date(2023, 6, 1).getTime(); // Start from July 2023
    //   const oneDay = 24 * 3600 * 1000;
    //   const cveGenerated: ChartDataPoint[] = [];

    //   let cveValue = 60;

    //   cveGenerated.push({ x: base, y: cveValue });

    //   for (let i = 1; i < 180; i++) {
    //     const now = base + i * oneDay;

    //     cveValue = Math.max(
    //       20,
    //       Math.min(80, Math.round((Math.random() - 0.5) * 15 + cveValue))
    //     );

    //     cveGenerated.push({ x: now, y: cveValue });
    //   }

    //   // Ensure mock data is sorted by time
    //   cveGenerated.sort((a, b) => a.x - b.x);
    //   setCVEData(cveGenerated);
    // };

    fetchData();
  }, [trigger]);

  // Chart options with the same styling as CyberResilienceView
  const chartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "area",
      stacked: false,
      // height: isTablet ? 250 : 350,
      zoom: {
        type: "x",
        enabled: true,
        autoScaleYaxis: true,
      },
      toolbar: {
        show: false,
      },
      background: "transparent",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 1.5,
    },
    markers: {
      size: 0,
      hover: {
        size: 5
      }
    },
    title: {
      text: "",
      align: "left",
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: isDarkMode ? 0.4 : 0.5,
        opacityTo: isDarkMode ? 0.05 : 0.1,
        stops: [0, 90, 100],
      },
    },
    grid: {
      borderColor: isDarkMode ? "#333333" : "#e7e7e7",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      type: "datetime",
      labels: {
        style: {
          colors: isDarkMode ? "#b3b3b3" : "#6B7280",
        },
        // Show full date on x-axis ticks as: dd mm yy
        formatter: (value: number) => {
          try {
            // Show day, short month name and two-digit year, e.g. "05 Jan 25"
            return moment(value).format("MMM  'YY");
          } catch (e) {
            return String(value);
          }
        },
        datetimeFormatter: {
          year: "yyyy",
          month: "MMM",
          day: "dd MMM",
          hour: "HH:mm",
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      title: {
        text: "",
      },
      labels: {
        style: {
          colors: isDarkMode ? "#b3b3b3" : "#6B7280",
        },
        formatter: (value) => `${value.toFixed(0)}`,
      },
    },
    tooltip: {
      shared: true,
      x: {
        format: "dd MMM yyyy",
      },
      y: {
        formatter: (val) => `${val.toFixed(0)}`,
      },
      theme: isDarkMode ? "dark" : "light",
    },
    colors: ["#FF6B00"],
    legend: {
      show: false,
    },
    responsive: [
      {
        breakpoint: 600,
        options: {
          chart: {
            height: 150,
          },
          title: {
            fontSize: "14px",
          },
        },
      },
    ],
  };

  const series = [
    {
      name: "CVE Score",
      data: cveData,
    },
  ];

  // Get latest score for display
  const getLatestScore = () => {
    return cveData.length > 0 ? Math.round(cveData[cveData.length - 1].y) : 0;
  };

  // Show skeleton loader if loading (internal or external)
  if (loading || externalLoading) {
    return (
      <Box sx={{ width: '100%', height: height || 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Skeleton variant="rectangular" width="100%" height={100} />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: 0, 
        bgcolor: isDarkMode ? "background.paper" : "white", 
        borderRadius: 1, 
        overflow: "hidden",
        // boxShadow: isDarkMode 
        //   ? "0px 4px 20px rgba(0, 0, 0, 0.3)" 
        //   : "0px 4px 20px rgba(0, 0, 0, 0.05)",
        height: height,
      }}
    >
      {loading ? (
         <Box
         sx={{
           width: '100%',
           minHeight: 100,
           height: height || 150,
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           position: 'relative',
           bgcolor: 'background.paper',
           borderRadius: 2,
           overflow: 'hidden'
         }}
       >
         {/* X-axis */}
         <Skeleton
           variant="rectangular"
           width="90%"
           height={8}
           sx={{
             position: 'absolute',
             bottom: 16,
             left: '5%',
             borderRadius: 2,
             bgcolor: 'rgba(147, 112, 219, 0.1)'
           }}
         />
         {/* Y-axis */}
         <Skeleton
           variant="rectangular"
           width={8}
           height="80%"
           sx={{
             position: 'absolute',
             left: 16,
             top: '10%',
             borderRadius: 2,
             bgcolor: 'rgba(147, 112, 219, 0.1)'
           }}
         />
         {/* Simulated line/area */}
         <Skeleton
           variant="rectangular"
           width="80%"
           height={100}
           sx={{
             position: 'absolute',
             left: '10%',
             bottom: 32,
             borderRadius: 8,
             transform: 'skewY(-10deg)',
             bgcolor: 'rgba(147, 112, 219, 0.15)'
           }}
         />
       </Box>
      ) : (
        <Box sx={{ height: "100%" }}>
          <Box sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            pt: 0,
            pb: 0,
            pr: 2,
            borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#f0f0f0'}`
          }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src= {isDarkMode ? darklogo : cyberResilienceImage}
                sx={{
                  // width: 200,
                  height: 20,
                  borderRadius: 0,
                  ml: 1,
                  objectFit: "contain"
                }}
              />
              {/* <Typography sx={{ 
                fontWeight: 600, 
                fontSize: "14px",
              }}>
                -CVE
              </Typography> */}
            </Box>
            {/* <Box sx={{ 
              display: "flex", 
              alignItems: "center",
              color: isDarkMode ? "#b3b3b3" : "#6B7280"
            }}>
              <Box component="span" sx={{ 
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#FF6B00",
                mr: 1
              }} />
              <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
                {getLatestScore()}
              </Typography>
            </Box> */}
          </Box>

          <Box sx={{ height: 120, pt: 0, pb: 0, pl: 2, pr: 2 }}>
            <ReactApexChart
              options={{
                ...chartOptions,
                grid: {
                  ...chartOptions.grid,
                  borderColor: isDarkMode ? "#333333" : "#f0f0f0",
                  xaxis: {
                    lines: {
                      show: true
                    }
                  },
                  yaxis: {
                    lines: {
                      show: true
                    }
                  }
                },
                yaxis: {
                  ...chartOptions.yaxis,
                  min: 0,
                  max: 100
                }
              }}
              series={series}
              type="area"
              height="100%"
              width="100%"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CVETimelineView; 